import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../utils/supabase.js';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StorageService from '../../../utils/StorageService';
import { generateGoogleCalendarLink, generateOutlookCalendarLink, generateICSContent } from '../../../utils/calendarGenerator';

const EmailPreview = ({ 
  emailData, 
  contractData, 
  selectedData,
  dealId: propDealId,
  onCopyToClipboard,
  onTestCalendarLinks 
}) => {
  // Get dealId from router params if not provided as prop
  const routerParams = useParams();
  const dealId = propDealId || routerParams.dealId;

  // State for data fetching
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refetchKey, setRefetchKey] = useState(0);

  // State for UI
  const [copySuccess, setCopySuccess] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Refs for preventing duplicate notifications
  const lastToastRef = useRef(null);

  // Force refetch function
  const forceRefetch = () => setRefetchKey(k => k + 1);

  // Fetch draft snapshot exactly once per dealId
  useEffect(() => {
    if (!dealId) return;
    
    const abortController = new AbortController();
    let mounted = true;

    const fetchDraftSnapshot = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load latest draft snapshot
        const { data: draftData, error: draftError } = await supabase
          .from('draft_snapshots')
          .select('*')
          .eq('deal_id', dealId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (draftError) {
          // Fallback to baseline snapshot
          const { data: baselineData, error: baselineError } = await supabase
            .from('baseline_snapshots')
            .select('*')
            .eq('deal_id', dealId)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          
          if (baselineError) {
            throw new Error('No snapshot found for this deal');
          }
          
          if (!mounted) return;
          setSnapshot(baselineData);
        } else {
          if (!mounted) return;
          setSnapshot(draftData);
        }
      } catch (e) {
        if (!mounted) return;
        const errorMessage = e instanceof Error ? e.message : 'Failed to fetch draft snapshot';
        setError(errorMessage);
        // IMPORTANT: do NOT retry here; let user click retry button
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchDraftSnapshot();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [dealId, refetchKey]);

  // Show notification toast only once per error message
  useEffect(() => {
    if (!error) return;
    if (lastToastRef.current === error) return;
    
    lastToastRef.current = error;
    
    const notification = {
      id: Date.now(),
      type: 'error',
      title: 'Load Error',
      message: error,
      duration: 5000
    };
    
    setNotifications(prev => [notification, ...prev]);
    
    // Auto-dismiss
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  }, [error]);

  // Helper function to extract first names (moved before useMemo)
  const getFirstNames = (fullNames) => {
    if (!fullNames) return 'Client';

    let names = [];

    // Split by common separators: "and", "&", ","
    if (fullNames.includes(' and ')) {
      names = fullNames.split(/\s+and\s+/i);
    } else if (fullNames.includes(' & ')) {
      names = fullNames.split(/\s*&\s*/);
    } else if (fullNames.includes(', ')) {
      names = fullNames.split(/\s*,\s*/);
    } else {
      names = [fullNames];
    }

    const firstNames = names.map(name => {
      const parts = name.trim().split(' ');
      return parts[0];
    }).filter(name => name && name.length > 0);

    if (firstNames.length === 0) {
      return 'Client';
    } else if (firstNames.length === 1) {
      return firstNames[0];
    } else if (firstNames.length === 2) {
      return `${firstNames[0]} & ${firstNames[1]}`;
    } else {
      return `${firstNames[0]}, ${firstNames[1]} & others`;
    }
  };

  // Helper function to generate styled HTML email (moved before useMemo)
  const generateStyledHTMLEmail = (emailData, contractData, selectedItems, dealId) => {
    const { representationRole } = emailData;
    
    // Extract contract data
    const extractedData = contractData?.extractedData || {};
    const property = extractedData.property || {};
    const parties = extractedData.parties || {};
    
    // Determine client first names based on representation role
    const clientNames = representationRole === 'buyer' ? parties.buyer : parties.seller;
    const clientFirstNames = getFirstNames(clientNames);
    
    // Use provided dealId or generate from contract data
    const clientDealId = dealId || contractData?.id || contractData?.contract_filename?.replace(/\.[^/.]+$/, "") || 'demo-deal';
    const calendarUrl = `https://cal.aire.systems/e/${clientDealId}?view=client`;

    // Build styled HTML email content
    let htmlContent = `<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px;">
    
    <p>Congratulations, ${clientFirstNames}!</p>
    
    <p>${property.address || 'The property'} is officially under contract—what an exciting milestone! Here's everything you need to know, all in one place:</p>
    
    <h3><strong>PARTIES</strong></h3>
    <ul>
        <li>Buyer: ${parties.buyer || 'Not specified'}</li>
        <li>Seller: ${parties.seller || 'Not specified'}</li>
    </ul>
    
    <h3><strong>MARK YOUR CALENDAR</strong></h3>
    <ul>`;

    // Add dates from selectedItems or fallback to extractedData dates
    const datesToShow = selectedItems?.milestones || [];
    
    if (datesToShow.length > 0) {
      datesToShow.forEach(milestone => {
        const dateValue = new Date(milestone.start_dt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        });
        
        // Format date for calendar links (MM/DD/YYYY)
        const calendarDate = new Date(milestone.start_dt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit', 
          day: '2-digit'
        });
        
        const eventTitle = `${property.address} - ${milestone.title}`;
        const googleLink = generateGoogleCalendarLink(eventTitle, calendarDate, null, `Contract deadline: ${milestone.title}`);
        const outlookLink = generateOutlookCalendarLink(eventTitle, calendarDate, null, `Contract deadline: ${milestone.title}`);
        
        htmlContent += `
        <li>
            ${milestone.title}: ${dateValue}
            <br>
            <div style="margin: 8px 0; font-size: 12px;">
              <a href="${googleLink}" target="_blank" style="color: #4285F4; text-decoration: none; margin-right: 10px;">Google Calendar</a>
              <a href="${outlookLink}" target="_blank" style="color: #0078D4; text-decoration: none; margin-right: 10px;">Outlook Calendar</a>
              <a href="data:text/calendar;charset=utf8,${encodeURIComponent(generateICSContent([{title: eventTitle, date: calendarDate, time: null, description: `Contract deadline: ${milestone.title}`}], property.address)?.content || '')}" download="${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}.ics" style="color: #28a745; text-decoration: none;">Download ICS</a>
            </div>
        </li>`;
      });
    } else {
      // Fallback to legacy dates format if no selectedItems
      const dates = extractedData.dates || {};
      Object.entries(dates).forEach(([key, dateInfo]) => {
        if (dateInfo?.value) {
          const displayName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          const eventTitle = `${property.address} - ${displayName}`;
          const googleLink = generateGoogleCalendarLink(eventTitle, dateInfo.value, null, `Contract deadline: ${displayName}`);
          const outlookLink = generateOutlookCalendarLink(eventTitle, dateInfo.value, null, `Contract deadline: ${displayName}`);
          
          htmlContent += `<li>${displayName}: ${dateInfo.value}
            <br>
            <div style="margin: 8px 0; font-size: 12px;">
              <a href="${googleLink}" target="_blank" style="color: #4285F4; text-decoration: none; margin-right: 10px;">Google Calendar</a>
              <a href="${outlookLink}" target="_blank" style="color: #0078D4; text-decoration: none; margin-right: 10px;">Outlook Calendar</a>
              <a href="data:text/calendar;charset=utf8,${encodeURIComponent(generateICSContent([{title: eventTitle, date: dateInfo.value, time: null, description: `Contract deadline: ${displayName}`}], property.address)?.content || '')}" download="${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}.ics" style="color: #28a745; text-decoration: none;">Download ICS</a>
            </div>
          </li>`;
        }
      });
    }

    htmlContent += `
    </ul>`;

    // Add deposits from selectedItems or fallback to extractedData
    const depositsToShow = selectedItems?.deposits || [];

    if (depositsToShow.length > 0) {
      htmlContent += `
    <h3><strong>DEPOSITS DUE</strong></h3>
    <ul>`;
      depositsToShow.forEach(deposit => {
        htmlContent += `
        <li>${deposit.description}</li>`;
      });
      htmlContent += `
    </ul>`;
    } else {
      // Fallback for legacy format - handle both array and string
      const financial = extractedData.financial || {};
      const deposits = financial.deposits;
      const depositsArray = Array.isArray(deposits) ? deposits : [deposits || extractedData.escrow?.earnestMoney || 'Not specified'];

      htmlContent += `
    <h3><strong>DEPOSITS DUE</strong></h3>
    <ul>`;
      depositsArray.forEach((deposit, index) => {
        if (deposit && deposit !== 'Not specified') {
          htmlContent += `
        <li>${deposit}</li>`;
        }
      });
      htmlContent += `
    </ul>`;
    }

    // Add additional terms from selectedItems or fallback to extractedData
    const termsToShow = selectedItems?.additionalTerms || [];
    
    if (termsToShow.length > 0) {
      htmlContent += `
    <h3><strong>ADDITIONAL TERMS</strong></h3>
    <ul>`;
      termsToShow.forEach(term => {
        htmlContent += `
        <li>${term.description}</li>`;
      });
      htmlContent += `
    </ul>`;
    } else {
      // Fallback for legacy format
      const additionalTerms = extractedData.nonStandardClauses || [];
      if (additionalTerms.length > 0) {
        htmlContent += `
    <h3><strong>ADDITIONAL TERMS</strong></h3>
    <ul>`;
        additionalTerms.forEach(term => {
          htmlContent += `
        <li>${term}</li>`;
        });
        htmlContent += `
    </ul>`;
      }
    }

    htmlContent += `
    <p>You'll be kept informed every step of the way. Questions? Just hit reply—you'll get a quick response.</p>
    
    <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border-radius: 8px; border: 1px solid #e9ecef;">
        <p style="margin: 0 0 10px 0; font-weight: bold; color: #333;">Add All Dates to Your Calendar</p>
        <p style="margin: 0;">
            <a href="${calendarUrl}" target="_blank"
               style="display: inline-block; background-color: #000000; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 14px; margin-right: 10px;">View Full Calendar</a>
        </p>
        <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">Individual calendar links are available next to each deadline above.</p>
    </div>
    
</div>`;

    return htmlContent;
  };

  // Helper function to generate formatted preview content for display (moved before useMemo)
  const generatePreviewContent = (emailData, contractData, selectedItems) => {
    const { representationRole } = emailData;
    
    // Extract contract data
    const extractedData = contractData?.extractedData || {};
    const property = extractedData.property || {};
    const parties = extractedData.parties || {};
    const financial = extractedData.financial || {};
    
    // Determine client first names based on representation role
    const clientNames = representationRole === 'buyer' ? parties.buyer : parties.seller;
    const clientFirstNames = getFirstNames(clientNames);
    
    // Build preview data
    const previewDates = {};
    const datesToShow = selectedItems?.milestones || [];
    
    if (datesToShow.length > 0) {
      datesToShow.forEach(milestone => {
        const key = milestone.type || 'customDate';
        previewDates[key] = {
          value: new Date(milestone.start_dt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        };
      });
    } else {
      // Fallback to legacy dates
      Object.assign(previewDates, extractedData.dates || {});
    }

    // Handle deposits - support both array and string formats
    const deposits = financial.deposits;
    const depositsArray = Array.isArray(deposits) ? deposits : [deposits];
    const previewDeposits = selectedItems?.deposits?.map(d => d.description) || depositsArray.filter(d => d && d !== 'Not specified');
    const previewTerms = selectedItems?.additionalTerms?.map(t => t.description) || extractedData.nonStandardClauses || [];
    
    return {
      subject: `Contract Update - ${property.address || 'Your Property'}`,
      greeting: `Congratulations, ${clientFirstNames}!`,
      opening: `${property.address || 'The property'} is officially under contract—what an exciting milestone! Here's everything you need to know, all in one place:`,
      parties: { buyer: parties.buyer || 'Not specified', seller: parties.seller || 'Not specified' },
      dates: previewDates,
      deposits: previewDeposits,
      additionalTerms: previewTerms,
      closing: `You'll be kept informed every step of the way. Questions? Just hit reply—you'll get a quick response.`,
      propertyAddress: property.address
    };
  };

  // Derive selected items from snapshot using useMemo (no state mutation in render)
  const selectedItems = useMemo(() => {
    if (selectedData) {
      return selectedData; // Use prop if provided
    }

    if (snapshot?.selected_data) {
      return snapshot.selected_data; // Use fetched data
    }

    return {
      milestones: [],
      deposits: [],
      additionalTerms: []
    };
  }, [selectedData, snapshot]);

  // Generate email content using useMemo to avoid re-computation
  const generatedEmail = useMemo(() => {
    if (!emailData || !contractData) return '';
    return generateStyledHTMLEmail(emailData, contractData, selectedItems, dealId);
  }, [emailData, contractData, selectedItems, dealId]);

  // Generate preview content using useMemo
  const previewContent = useMemo(() => {
    if (!emailData || !contractData) return null;
    return generatePreviewContent(emailData, contractData, selectedItems);
  }, [emailData, contractData, selectedItems]);

  // Helper function to create ICS download link
  const createICSDownloadLink = (title, date, time = null, description = '') => {
    try {
      // Create single event for ICS
      const events = [{
        title,
        date,
        time,
        description
      }];
      
      const icsResult = generateICSContent(events, title);
      if (!icsResult) return '#';
      
      // Create blob and data URL for download
      const blob = new Blob([icsResult.content], { type: 'text/calendar;charset=utf-8' });
      const dataUrl = URL.createObjectURL(blob);
      
      return dataUrl;
    } catch (error) {
      console.error('Error creating ICS download link:', error);
      return '#';
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([generatedEmail], { type: 'text/html' }),
          'text/plain': new Blob([generatedEmail.replace(/<[^>]*>/g, '')], { type: 'text/plain' })
        })
      ]);
      
      setCopySuccess(true);
      onCopyToClipboard?.(generatedEmail);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
      try {
        await navigator.clipboard.writeText(generatedEmail);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error('All copy methods failed:', fallbackErr);
      }
    }
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Render loading state
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm">
        <div className="p-6 text-center">
          <Icon name="Loader" className="animate-spin mx-auto mb-4" size={48} />
          <p>Loading email preview...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm">
        <div className="p-6 text-center">
          <Icon name="AlertTriangle" size={48} className="mx-auto mb-4 text-red-500" />
          <h3 className="text-lg font-semibold mb-2">Failed to Load Email Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={forceRefetch} className="bg-primary hover:bg-primary/90">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
              <Icon name="Mail" size={20} color="var(--color-secondary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold" style={{color: '#0d1519'}}>
                Client Email Preview
              </h2>
              <p className="text-sm" style={{color: '#6b7280'}}>
                Ready to copy and send to your client
              </p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={async () => {
                const filename = contractData?.contract_filename;

                try {
                  const result = await StorageService.getContractUrl(filename, dealId);

                  if (result.success && result.url) {
                    const link = document.createElement('a');
                    link.href = result.url;
                    link.download = filename || 'contract.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  } else {
                    alert(`Original PDF file not available for download.\n\n${result.message || 'File not found'}`);
                  }
                } catch (error) {
                  console.error('Download error:', error);
                  alert('Unable to download original document - file may have been too large to store locally.');
                }
              }}
              className="bg-black text-white px-4 py-2 rounded text-sm hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
            >
              Download Contract
            </button>
            
            <button
              onClick={handleCopyToClipboard}
              disabled={!generatedEmail}
              className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{backgroundColor: copySuccess ? '#22c55e' : '#f97316'}}
            >
              {copySuccess ? '✓ Copied!' : 'Copy HTML Email'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {previewContent ? (
          <div className="space-y-6">
            {/* Email Subject */}
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">SUBJECT</h3>
              <p className="text-lg font-semibold" style={{color: '#0d1519'}}>{previewContent.subject}</p>
            </div>

            {/* Email Body Preview */}
            <div className="bg-white border rounded-lg p-6" style={{maxWidth: '600px', margin: '0 auto'}}>
              <div className="space-y-4">
                <p className="text-gray-800">{previewContent.greeting}</p>
                
                <p className="text-gray-800">{previewContent.opening}</p>
                
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">PARTIES</h4>
                  <ul className="list-disc list-inside text-gray-800 space-y-1">
                    <li>Buyer: {previewContent.parties.buyer}</li>
                    <li>Seller: {previewContent.parties.seller}</li>
                  </ul>
                </div>

                {/* Calendar Section */}
                {Object.keys(previewContent.dates).length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2">MARK YOUR CALENDAR</h4>
                    <ul className="list-disc list-inside text-gray-800 space-y-3">
                      {Object.entries(previewContent.dates).map(([key, dateInfo]) => {
                        if (!dateInfo?.value) return null;
                        const displayName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                        const eventTitle = `${previewContent.propertyAddress} - ${displayName}`;
                        const googleLink = generateGoogleCalendarLink(eventTitle, dateInfo.value, null, `Contract deadline: ${displayName}`);
                        const outlookLink = generateOutlookCalendarLink(eventTitle, dateInfo.value, null, `Contract deadline: ${displayName}`);
                        
                        return (
                          <li key={key} className="mb-2">
                            <div>{displayName}: {dateInfo.value}</div>
                            <div className="text-xs mt-1 space-x-2">
                              <a href={googleLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Calendar</a>
                              <a href={outlookLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Outlook Calendar</a>
                              <a 
                                href={`data:text/calendar;charset=utf8,${encodeURIComponent(generateICSContent([{title: eventTitle, date: dateInfo.value, time: null, description: `Contract deadline: ${displayName}`}], previewContent.propertyAddress)?.content || '')}`}
                                download={`${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}.ics`}
                                className="text-green-600 hover:underline"
                              >
                                Download ICS
                              </a>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Deposits Section */}
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">DEPOSITS DUE</h4>
                  <ul className="list-disc list-inside text-gray-800">
                    {Array.isArray(previewContent.deposits) ? (
                      previewContent.deposits.map((deposit, index) => (
                        <li key={index}>{deposit}</li>
                      ))
                    ) : (
                      <li>{previewContent.deposits}</li>
                    )}
                  </ul>
                </div>

                {/* Additional Terms */}
                {previewContent.additionalTerms.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2">ADDITIONAL TERMS</h4>
                    <ul className="list-disc list-inside text-gray-800 space-y-1">
                      {previewContent.additionalTerms.map((term, index) => (
                        <li key={index}>{term}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-gray-800">{previewContent.closing}</p>

                {/* Calendar Download */}
                {Object.keys(previewContent.dates).length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                    <p className="font-bold text-gray-800 mb-2">Add All Dates to Your Calendar</p>
                    <a 
                      href={`https://cal.aire.systems/e/${dealId}?view=client`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black text-white px-6 py-3 rounded-lg font-bold text-sm hover:opacity-90 transition-opacity inline-block mr-3 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      View Full Calendar
                    </a>
                    <p className="text-xs text-gray-600 mt-2">Individual calendar links are available next to each deadline above.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Instructions</h4>
              <p className="text-blue-800 text-sm">
                Click "Copy HTML Email" above to copy the styled email with individual calendar links for each deadline. 
                Each deadline includes Google Calendar, Outlook, and ICS download links. 
                Paste directly into your email client to send to your client.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Mail" size={48} color="#ccc" className="mx-auto mb-4" />
            <p className="text-gray-500">No email data available</p>
          </div>
        )}
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 space-y-2 z-50">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-4 rounded-lg shadow-lg max-w-sm ${
                notification.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium">{notification.title}</h4>
                  <p className="text-sm mt-1">{notification.message}</p>
                </div>
                <button
                  onClick={() => dismissNotification(notification.id)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailPreview;